import * as config from '../../lib/config';
import { sendCommand } from '../../actions/commandActions';
import { toast } from 'react-toastify';
import Auth from '../../auth/auth';

import {
  ADD_PROPOSAL_FAILURE,
  ADD_PROPOSAL_START,
  ADD_PROPOSAL_SUCCESS,
  CALCULATE_PROPOSALS_START,
  CHANGE_ADJUSTMENT_VALUE,
  CLEAR_WORKSPACE,
  COPY_PROPOSAL_FAILURE,
  COPY_PROPOSAL_START,
  COPY_PROPOSAL_SUCCESS,
  DELETE_PROPOSAL_FAILURE,
  DELETE_PROPOSAL_START,
  DELETE_PROPOSAL_SUCCESS,
  DISCARD_PROPOSAL_CHANGES,
  GET_WORKSPACE_FAILURE,
  GET_WORKSPACE_START,
  GET_WORKSPACE_SUCCESS,
  RENAME_PROPOSAL_FAILURE,
  RENAME_PROPOSAL_START,
  RENAME_PROPOSAL_SUCCESS,
  UPDATE_PROPOSAL_FAILURE,
  UPDATE_PROPOSAL_START,
  UPDATE_PROPOSAL_SUCCESS,
  PRORATE_RESULTS_START,
  PRORATE_RESULTS_SUCCESS,
  PRORATE_RESULTS_FAILURE,
} from './types';
import * as normalization from './workspace-normalization';

// Actions
const getWorkspaceStart = () => ({
  type: GET_WORKSPACE_START,
});

const getWorkspaceSuccess = (res) => ({
  type: GET_WORKSPACE_SUCCESS,
  payload: res,
});

const getWorkspaceFailure = (error) => ({
  type: GET_WORKSPACE_FAILURE,
  payload: error,
});

const addProposalStart = () => ({
  type: ADD_PROPOSAL_START,
});

const addProposalSuccess = (res) => ({
  type: ADD_PROPOSAL_SUCCESS,
  payload: res,
});

const addProposalFailure = (error) => ({
  type: ADD_PROPOSAL_FAILURE,
  payload: error,
});

const updateProposalStart = () => ({
  type: UPDATE_PROPOSAL_START,
});

const updateProposalSuccess = (res) => ({
  type: UPDATE_PROPOSAL_SUCCESS,
  payload: res,
});

const updateProposalFailure = (error) => ({
  type: UPDATE_PROPOSAL_FAILURE,
  payload: error,
});

export const copyProposalStart = () => ({
  type: COPY_PROPOSAL_START,
});

export const copyProposalSuccess = (res) => ({
  type: COPY_PROPOSAL_SUCCESS,
  payload: res,
});

export const copyProposalFailure = (error) => ({
  type: COPY_PROPOSAL_FAILURE,
  payload: error,
});

export const renameProposalStart = () => ({
  type: RENAME_PROPOSAL_START,
});

export const renameProposalSuccess = (res) => ({
  type: RENAME_PROPOSAL_SUCCESS,
  payload: res,
});

export const renameProposalFailure = (error) => ({
  type: RENAME_PROPOSAL_FAILURE,
  payload: error,
});

export const deleteProposalStart = () => ({
  type: DELETE_PROPOSAL_START,
});

export const deleteProposalSuccess = (res) => ({
  type: DELETE_PROPOSAL_SUCCESS,
  payload: res,
});

export const deleteProposalFailure = (error) => ({
  type: DELETE_PROPOSAL_FAILURE,
  payload: error,
});

export const discardProposalChanges = () => ({
  type: DISCARD_PROPOSAL_CHANGES,
});

export const clearWorkspace = () => ({
  type: CLEAR_WORKSPACE,
});

export const calculateProposalsStart = (req) => ({
  type: CALCULATE_PROPOSALS_START,
  payload: req,
});

export const calculateProposalsClear = () => ({
  type: 'CALCULATE_PROPOSALS_CLEAR',
});

export const prorateResultsStart = ({ momentSignature, prorationType }) => ({
  type: PRORATE_RESULTS_START,
  payload: { momentSignature, prorationType },
});

export const prorateResultsSuccess = (proposalResult) => ({
  type: PRORATE_RESULTS_SUCCESS,
  payload: proposalResult,
});

export const prorateResultsFailure = (error) => ({
  type: PRORATE_RESULTS_FAILURE,
  payload: error,
});

export const changeAdjustmentValue = (
  currentProposal,
  adjustmentId,
  values,
  table,
  aggCostTableId,
  payGradeMinMax,
  pooledInput,
) => {
  return {
    type: CHANGE_ADJUSTMENT_VALUE,
    payload: {
      currentProposal,
      adjustmentId,
      values,
      table,
      aggCostTableId,
      payGradeMinMax,
      pooledInput,
    },
  };
};

// Error middleware
function handleFetchError(response) {
  if (!response.ok) {
    throw Error('Failed to retrieve workspace.');
  }
  return response;
}

function handleAddError(response, toastId) {
  if (!response.ok) {
    if (toastId) {
      toast.update(toastId, {
        render: 'Failed to add group to project',
        type: toast.TYPE.ERROR,
        autoClose: 3000,
      });
    }
    const error = {
      code: response.status,
      error: new Error('Failed to add proposal'),
    };
    throw error;
  }
  return response;
}

function handleUpdateError(response, toastId) {
  if (!response.ok) {
    if (toastId) {
      toast.update(toastId, {
        render: 'Failed to add group to project',
        type: toast.TYPE.ERROR,
        autoClose: 3000,
      });
    }
    const error = {
      code: response.status,
      error: new Error('Failed to update proposal'),
    };
    throw error;
  }
  return response;
}

function handleRenameError(response, toastId) {
  if (!response.ok) {
    if (toastId) {
      toast.update(toastId, {
        render: 'Failed to rename proposal',
        type: toast.TYPE.ERROR,
        autoClose: 3000,
      });
    }
    const error = {
      code: response.status,
      error: new Error('Failed to rename proposal'),
    };
    throw error;
  }
  return response;
}

function handleDeleteError(response, toastId) {
  if (!response.ok) {
    if (toastId) {
      toast.update(toastId, {
        render: 'Failed to delete proposal',
        type: toast.TYPE.ERROR,
        autoClose: 3000,
      });
    }
    const error = {
      code: response.status,
      error: new Error('Failed to delete proposal'),
    };
    throw error;
  }
  return response;
}

// API actions

export const getWorkspace =
  (tenantId, projectId, updateToast) => (dispatch) => {
    dispatch(getWorkspaceStart());
    new Auth().checkAuth();
    return fetch(
      `${config.baseURL()}/api/tenants/${tenantId}/projects/${projectId}/workspace`,
      {
        method: 'GET',
        credentials: 'include',
      },
    )
      .then(handleFetchError)
      .then((res) => res.json())
      .then((workspace) => {
        // NORMALIZE DATA FOR REDUCER STATE
        let normalizedData = {
          ...workspace,
          appliedDemographicFilters: {},
          selectedDemographicFilters: {},
          normalizedProposals: normalization.normalizeProposals(workspace),
          normalizedGroups: normalization.normalizeGroups(workspace),
          normalizedAdjustments: normalization.normalizeAdjustments(workspace),
        };
        dispatch(getWorkspaceSuccess(normalizedData));
        if (updateToast) {
          toast.update(updateToast.toastId, {
            render: updateToast.message,
            type: toast.TYPE[updateToast.type],
            autoClose: 3000,
          });
        }
      })
      .catch((error) => {
        dispatch(getWorkspaceFailure(error));
      });
  };

export const addProposal = (proposalData) => (dispatch) => {
  let toastId = null;
  let toastNotify = () =>
    (toastId = toast('Adding new proposal...', { autoClose: false }));

  toastNotify();
  dispatch(addProposalStart());
  new Auth().checkAuth();
  return fetch(`${config.baseURL()}/api/commands`, {
    method: 'POST',
    credentials: 'include',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(proposalData),
  })
    .then((res) => handleAddError(res, toastId))
    .then((res) => {
      let updateToast = {
        toastId,
        message: 'Proposal added',
        type: 'SUCCESS',
      };

      dispatch(addProposalSuccess(res));
      dispatch(
        getWorkspace(
          proposalData.tenantId,
          proposalData.projectId,
          updateToast,
        ),
      );
    })
    .catch((error) => {
      dispatch(addProposalFailure(error));
    });
};

export const updateProposal = (proposalData) => (dispatch) => {
  let toastId = null;
  let toastNotify = () =>
    (toastId = toast('Saving proposal...', { autoClose: false }));

  toastNotify();

  dispatch(updateProposalStart());
  new Auth().checkAuth();
  return fetch(`${config.baseURL()}/api/commands`, {
    method: 'POST',
    credentials: 'include',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(proposalData),
  })
    .then((res) => handleUpdateError(res, toastId))
    .then((res) => {
      let updateToast = {
        toastId,
        message: 'Proposal saved',
        type: 'SUCCESS',
      };
      dispatch(updateProposalSuccess(res));
      dispatch(
        getWorkspace(
          proposalData.tenantId,
          proposalData.projectId,
          updateToast,
        ),
      );
    })
    .catch((error) => {
      dispatch(updateProposalFailure(error));
    });
};

export const renameProposal = (proposalData) => (dispatch) => {
  let toastId = null;
  let toastNotify = () =>
    (toastId = toast('Renaming proposal...', { autoClose: false }));

  toastNotify();

  dispatch(renameProposalStart());
  new Auth().checkAuth();
  return fetch(`${config.baseURL()}/api/commands`, {
    method: 'POST',
    credentials: 'include',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(proposalData),
  })
    .then((res) => handleRenameError(res, toastId))
    .then((res) => {
      let updateToast = {
        toastId,
        message: 'Proposal renamed',
        type: 'SUCCESS',
      };
      dispatch(renameProposalSuccess(res));
      dispatch(
        getWorkspace(
          proposalData.tenantId,
          proposalData.projectId,
          updateToast,
        ),
      );
    })
    .catch((error) => {
      if (toastId) {
        toast.update(toastId, {
          render: 'Proposal rename failed',
          type: toast.TYPE.ERROR,
          autoClose: 3000,
        });
      }
      dispatch(renameProposalFailure(error));
    });
};

export const copyProposal = (proposalData) => (dispatch) => {
  let toastId = null;
  let toastNotify = () =>
    (toastId = toast('Copying proposal...', { autoClose: false }));

  toastNotify();

  dispatch(copyProposalStart());
  new Auth().checkAuth();
  return fetch(`${config.baseURL()}/api/commands`, {
    method: 'POST',
    credentials: 'include',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(proposalData),
  })
    .then((res) => handleAddError(res, toastId))
    .then((res) => {
      let updateToast = {
        toastId,
        message: 'Proposal copied',
        type: 'SUCCESS',
      };
      dispatch(copyProposalSuccess(res));
      dispatch(
        getWorkspace(
          proposalData.tenantId,
          proposalData.projectId,
          updateToast,
        ),
      );
    })
    .catch((error) => {
      dispatch(copyProposalFailure(error));
    });
};

export const deleteProposal = (proposalData) => (dispatch) => {
  let toastId = null;
  let toastNotify = () =>
    (toastId = toast('Deleting proposal...', { autoClose: false }));

  toastNotify();

  dispatch(deleteProposalStart());
  new Auth().checkAuth();
  return fetch(`${config.baseURL()}/api/commands`, {
    method: 'POST',
    credentials: 'include',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(proposalData),
  })
    .then((res) => handleDeleteError(res, toastId))
    .then((res) => {
      let updateToast = {
        toastId,
        message: 'Proposal deleted',
        type: 'SUCCESS',
      };
      dispatch(deleteProposalSuccess(res));
      dispatch(
        getWorkspace(
          proposalData.tenantId,
          proposalData.projectId,
          updateToast,
        ),
      );
    })
    .catch((error) => {
      dispatch(deleteProposalFailure(error));
    });
};

export const calculateProratedTable = (reportKey) => (dispatch) => {
  dispatch(prorateResultsStart(reportKey))
}

export const calculateProposals = (calculationReq) => (dispatch) => {
  dispatch(calculateProposalsStart(calculationReq));
};

export const setCalculationMethod = (calculationMethodData) => (dispatch) => {
  const { calculateOnUpdate } = calculationMethodData;
  const startLabel = calculateOnUpdate
    ? 'Enabling automatic calculations'
    : 'Disabling automatic calculations';
  const successLabel = calculateOnUpdate
    ? 'Enabled automatic calculations'
    : 'Disabled automatic calculations';
  const failLabel = calculateOnUpdate
    ? 'Failed to enable automatic calculations'
    : 'Failed to disable automatic calculations';
  const commandLabel = {
    start: startLabel,
    success: successLabel,
    fail: failLabel,
  };
  const successCallback = getWorkspace;
  dispatch(sendCommand(calculationMethodData, commandLabel, successCallback));
};
